import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { APPLICANT_ATTRIBUTION } from 'cpool-constants';
import { APPLICANT_ATTRIBUTIONS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import type { SchemaDemandedDocuments } from '~/features/leadDetails/schemas/demandedDocuments.schema';
import { createRequiredContext } from '~/utils/createRequiredContext';

type DemandDocumentsContextType = {
    demandedDocuments: SchemaDemandedDocuments;
    setDemandedDocuments: Dispatch<SetStateAction<SchemaDemandedDocuments>>;
    attribution: APPLICANT_ATTRIBUTION;
    setAttribution: Dispatch<SetStateAction<APPLICANT_ATTRIBUTION>>;
    resetDemandedDocuments: () => void;
    resetCount: number;
};

const [useDemandedDocumentsContext, ContextProvider] =
    createRequiredContext<DemandDocumentsContextType>('DemandedDocumentsContext');

type Props = {
    children: React.ReactNode;
};

const DemandedDocumentsContextProvider: React.FC<Props> = ({ children }) => {
    const { t } = useTranslation();
    const buildInitialDocuments = useCallback(
        (): SchemaDemandedDocuments => ({
            bankStatements: [], // is empty because the entries are created dynamically
            currentLoans: [
                { name: t('DOCUMENT_DEMANDER.CURRENT_LOANS_DOCUMENTS.ABLÖSEBESCHEINIGUNGEN'), requested: false },
                { name: t('DOCUMENT_DEMANDER.CURRENT_LOANS_DOCUMENTS.TILGUNGSPLAN'), requested: false },
                { name: t('DOCUMENT_DEMANDER.CURRENT_LOANS_DOCUMENTS.ERLEDIGUNGSSCHREIBEN'), requested: false },
                { name: t('DOCUMENT_DEMANDER.CURRENT_LOANS_DOCUMENTS.KREDITVERTRAG'), requested: false },
                { name: t('DOCUMENT_DEMANDER.CURRENT_LOANS_DOCUMENTS.ABZULÖSENDE KREDITVERTRÄGE'), requested: false },
                { name: t('DOCUMENT_DEMANDER.CURRENT_LOANS_DOCUMENTS.KREDITKONTOAUSZÜGE'), requested: false },
            ],
            individualDocuments: [], // is empty because the entries are created dynamically
            individualLoan: [
                { name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.GRUNDBUCHAUSSZUG'), requested: false },
                { name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.FLURKARTE'), requested: false },
                {
                    name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.CURRENT_CREDIT_CARD_STATEMENTS'),
                    requested: false,
                },
                {
                    name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.REAL_ESTATE_LOAN_CONTRACT_AND_ANNUAL_BALANCE'),
                    requested: false,
                },
                { name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.COPY_ID_CARDS'), requested: false },
                { name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.COPY_EC_CARD_MAIN_ACCOUNT'), requested: false },
                { name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.CHILD_NAME_AND_BIRTHDATE'), requested: false },
                {
                    name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.INTERIOR_EXTERIOR_PHOTOS_AND_HEATING'),
                    requested: false,
                },
                { name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.FLOOR_PLANS_BUILDING_PLANS'), requested: false },
                {
                    name: t(
                        'DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.LIVING_SPACE_CALCULATION_OR_EXTERIOR_DIMENSIONS',
                    ),
                    requested: false,
                },
                {
                    name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.EXPOSE_OR_COMPLETED_PROPERTY_FORM'),
                    requested: false,
                },
                { name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.ENERGY_CERTIFICATE'), requested: false },
                {
                    name: t('DOCUMENT_DEMANDER.INDIVIDUAL_LOAN_DOCUMENTS.PARTITION_DECLARATION_AND_ALLOCATION_PLAN'),
                    requested: false,
                },
            ],
            payslips: [], // is empty because the entries are created dynamically
            payslips_sidejob: [], // is empty because the entries are created dynamically
            pensionCertificate: [
                {
                    name: t('DOCUMENT_DEMANDER.PENSION_CERTIFICATE_DOCUMENTS.AKTUELLE_RENTENBESCHEID'),
                    requested: false,
                },
            ],
            residencePermit: [
                { name: t('DOCUMENT_DEMANDER.RESIDENCE_PERMIT_DOCUMENTS.AUFENTHALTSTITEL'), requested: false },
            ],
        }),
        [t],
    );
    const [demandDocuments, setDemandedDocuments] = useState<SchemaDemandedDocuments>(buildInitialDocuments);
    const [attribution, setAttribution] = useState<APPLICANT_ATTRIBUTION>(APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1);

    const [resetCount, setResetCount] = useState(0);

    const resetDemandedDocuments = useCallback(() => {
        setDemandedDocuments(buildInitialDocuments());
        setAttribution(APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1);
        setResetCount((count) => count + 1);
    }, [buildInitialDocuments]);

    const value = useMemo(
        () => ({
            attribution,
            demandedDocuments: demandDocuments,
            resetCount,
            resetDemandedDocuments,
            setAttribution,
            setDemandedDocuments,
        }),
        [attribution, demandDocuments, resetCount, resetDemandedDocuments],
    );

    return <ContextProvider value={value}>{children}</ContextProvider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export { useDemandedDocumentsContext };
export default DemandedDocumentsContextProvider;
